import axios from 'axios'
import { API_BASE_URL, createAuthenticatedApi, extractApiErrorMessage, handleUnauthorized } from '@/lib/api'

// Types
export interface Measurement {
  id: string
  customerId: string
  garmentType: 'JACKET_VEST' | 'SHERWANI' | 'SHIRT' | 'KURTA' | 'TROUSER' | 'KAFTAN' | 'ALTERATION'
  data: Record<string, any>
  createdAt: string
  updatedAt: string
  isActive: boolean
  customer?: {
    id: string
    name: string
    personalMobile: string
    email: string
  }
}

export interface CreateMeasurementRequest {
  customerId: string
  garmentType: 'JACKET_VEST' | 'SHERWANI' | 'SHIRT' | 'KURTA' | 'TROUSER' | 'KAFTAN' | 'ALTERATION'
  data: Record<string, any>
}

export interface UpdateMeasurementRequest {
  data: Record<string, any>
}

export interface MeasurementFilters {
  page?: number
  limit?: number
  customerId?: string
  garmentType?: string
}

const handleApiError = (error: any): never => {
  if (error.response?.status === 401) {
    handleUnauthorized()
  }
  
  const message = extractApiErrorMessage(error, 'An error occurred')
  throw new Error(message)
}

const measurementsApi = createAuthenticatedApi(`${API_BASE_URL}/measurements`)

// API functions
export const getMeasurements = async (filters: MeasurementFilters = {}): Promise<{
  data: Measurement[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}> => {
  try {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.customerId) params.append('customerId', filters.customerId)
    if (filters.garmentType) params.append('garmentType', filters.garmentType)

    const response = await measurementsApi.get(`?${params.toString()}`)
    return response.data
  } catch (error) {
    return handleApiError(error)
  }
}

export const getCustomerMeasurements = async (customerId: string, garmentType?: string): Promise<{
  customer: {
    id: string
    name: string
    personalMobile: string
    email: string
  }
  measurements: Record<string, Measurement[]>
}> => {
  try {
    const params = garmentType ? `?garmentType=${garmentType}` : ''
    const response = await measurementsApi.get(`/customer/${customerId}${params}`)
    return response.data.data
  } catch (error) {
    return handleApiError(error)
  }
}

export const getMeasurementById = async (id: string): Promise<Measurement> => {
  try {
    const response = await measurementsApi.get(`/${id}`)
    return response.data.data
  } catch (error) {
    return handleApiError(error)
  }
}

export const createMeasurement = async (measurementData: CreateMeasurementRequest): Promise<Measurement> => {
  try {
    const response = await measurementsApi.post('/', measurementData)
    return response.data.data
  } catch (error) {
    return handleApiError(error)
  }
}

export const updateMeasurement = async (id: string, measurementData: UpdateMeasurementRequest): Promise<Measurement> => {
  try {
    const response = await measurementsApi.put(`/${id}`, measurementData)
    return response.data.data
  } catch (error) {
    return handleApiError(error)
  }
}

export const deleteMeasurement = async (id: string): Promise<void> => {
  try {
    await measurementsApi.delete(`/${id}`)
  } catch (error) {
    return handleApiError(error)
  }
}

// Helper function to get garment type display name
export const getGarmentTypeDisplayName = (garmentType: string): string => {
  const displayNames: Record<string, string> = {
    'JACKET_VEST': 'Jacket & Vest',
    'SHERWANI': 'Sherwani',
    'SHIRT': 'Shirts',
    'KURTA': 'Kurta',
    'TROUSER': 'Trouser',
    'KAFTAN': 'Kaftans / Nigerian Set',
    'ALTERATION': 'Alteration'
  }
  return displayNames[garmentType] || garmentType
}

// Helper function to get garment type from display name
export const getGarmentTypeFromDisplayName = (displayName: string): string => {
  const typeMap: Record<string, string> = {
    'Jacket & Vest': 'JACKET_VEST',
    'Sherwani': 'SHERWANI',
    'Shirts': 'SHIRT',
    'Kurta': 'KURTA',
    'Trouser': 'TROUSER',
    'Kaftans / Nigerian Set': 'KAFTAN',
    'Alteration': 'ALTERATION'
  }
  return typeMap[displayName] || displayName
}
